import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  doc,
  getDoc
} from 'firebase/firestore';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import ShopLogo from "../images/shop_logo.png";
import { SHOP_NAME, SHOP_ADDRESS, SHOP_PHONE } from '../Variables.js';
import Modal from 'react-modal';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [dateRange, setDateRange] = useState("thisMonth");
  const [data, setData] = useState({
    summary: { revenue: 0, expenses: 0, profit: 0, cash: 0, assets: 0, liabilities: 0, netWorth: 0 },
    profitTrend: [],
    cashFlowTrend: [],
    topExpenses: [],
    recentTransactions: []
  });
  const [rankData, setRankData] = useState(null);
  const [loadingRank, setLoadingRank] = useState(false);

  const [payrollRecords, setPayrollRecords] = useState([]);
  const [loadingPayroll, setLoadingPayroll] = useState(false);
  const [selectedPaySlip, setSelectedPaySlip] = useState(null);
  const [isPaySlipModalOpen, setIsPaySlipModalOpen] = useState(false);
  const [salaryComponents, setSalaryComponents] = useState([]);

  const accountsRef = collection(db, "accounts");
  const entriesRef = collection(db, "journalEntries");
  const ranksRef = collection(db, "ranks");
  const payrollRecordsCollectionRef = collection(db, "payrollRecords");
  const salaryStructureCollectionRef = collection(db, "salaryStructure");

  useEffect(() => {
    if (user && userData) {
      setLoading(false);
      // Only load dashboard data for non-staff users
      if (userData.status === 'Staff' || userData.status === 'Cashier') {  
        loadRankData();
        loadPayrollRecords();
      } else {
        loadDashboard();
      }
    }
  }, [user, userData, dateRange]);

  // Also load payroll records when rank data is loaded
  useEffect(() => {
    if ((userData?.status === 'Staff' || userData?.status === 'Cashier') && rankData && salaryComponents.length > 0) {
      loadPayrollRecords();
    }
  }, [rankData, salaryComponents]);

  // Function to load rank data and salary components for staff users
  const loadRankData = async () => {
    if (!userData?.rank) return;
    
    try {
      setLoadingRank(true);
      
      // Fetch both rank and salary components in parallel
      const [rankSnapshot, salaryComponentsSnapshot] = await Promise.all([
        // Fetch rank
        getDocs(query(ranksRef, where("id", "==", userData.rank))),
        // Fetch salary components
        getDocs(salaryStructureCollectionRef)
      ]);
      
      // Process rank data
      if (!rankSnapshot.empty) {
        const rankDoc = rankSnapshot.docs[0];
        setRankData({
          id: rankDoc.id,
          ...rankDoc.data()
        });
      } else {
        console.warn("No rank found with ID:", userData.rank);
        const allRanksSnapshot = await getDocs(ranksRef);
        const allRanks = allRanksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const foundRank = allRanks.find(rank => rank.id === userData.rank || rank.rankId === userData.rank);
        if (foundRank) {
          setRankData(foundRank);
        }
      }
      
      // Process salary components
      if (!salaryComponentsSnapshot.empty) {
        const components = salaryComponentsSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        setSalaryComponents(components);
      }
      
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load staff information");
      setShowModal(true);
    } finally {
      setLoadingRank(false);
    }
  };

  const getDateRangeFilter = () => {
    const now = new Date();
    let start, end = now;
    if (dateRange === "thisMonth") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (dateRange === "lastMonth") {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    } else {
      start = new Date(now.getFullYear(), 0, 1);
    }
    return { start, end };
  };

  const loadDashboard = async () => {
    try {
      const { start, end } = getDateRangeFilter();
      const [accountsSnap, entriesSnap] = await Promise.all([
        getDocs(query(accountsRef)),
        getDocs(query(entriesRef))
      ]);

      const accounts = accountsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const entries = entriesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const filteredEntries = entries.filter(e => {
        const d = new Date(e.date);
        return d >= start && d <= end;
      });

      const monthlyData = {};
      const expenseByCategory = {};
      let totalRev = 0, totalExp = 0, cash = 0;

      filteredEntries.forEach(entry => {
        const monthKey = new Date(entry.date).toLocaleString('default', { month: 'short' });
        if (!monthlyData[monthKey]) monthlyData[monthKey] = { revenue: 0, expenses: 0 };
        
        entry.lines.forEach(line => {
          const amount = Number(line.debit) || Number(line.credit) || 0;
          const acc = accounts.find(a => a.id === line.accountId || a.name === line.accountName);
          if (!acc) return;

          const name = (acc.name || '').toLowerCase();
          const type = (acc.type || '').toLowerCase();

          if (type.includes('revenue') || name.includes('sales')) {
            monthlyData[monthKey].revenue += amount;
            totalRev += amount;
          }
          if (type.includes('expense') || name.includes('cost')) {
            monthlyData[monthKey].expenses += amount;
            totalExp += amount;
            const cat = acc.name.split(' ')[0];
            expenseByCategory[cat] = (expenseByCategory[cat] || 0) + amount;
          }
          if (name.includes('cash') || name.includes('bank')) cash += amount;
        });
      });

      const profitTrend = Object.keys(monthlyData).map(m => ({
        month: m,
        profit: monthlyData[m].revenue - monthlyData[m].expenses
      })).slice(-6);

      const topExpenses = Object.entries(expenseByCategory)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      const recentTxns = entries
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 6)
        .map(t => ({
          ...t,
          displayDate: new Date(t.date).toLocaleDateString('en-NG', { day: '2-digit', month: 'short' })
        }));

      setData({
        summary: {
          revenue: totalRev,
          expenses: totalExp,
          profit: totalRev - totalExp,
          cash: cash,
          assets: totalRev + cash,
          liabilities: 0,
          netWorth: totalRev + cash
        },
        profitTrend,
        cashFlowTrend: profitTrend,
        topExpenses,
        recentTransactions: recentTxns
      });
    } catch (err) {
      setError("Failed to load dashboard");
      setShowModal(true);
    }
  };

  // Function to load payroll records for staff users
  const loadPayrollRecords = async () => {
    if (!userData || !userData.id) return;
    
    try {
      setLoadingPayroll(true);
      
      // Calculate last 3 months
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      
      const last3Months = [];
      for (let i = 0; i < 3; i++) {
        const month = currentMonth - i;
        let year = currentYear;
        let adjustedMonth = month;
        
        if (month < 1) {
          adjustedMonth = month + 12;
          year = currentYear - 1;
        }
        
        const monthYear = `${year}-${String(adjustedMonth).padStart(2, '0')}`;
        last3Months.push(monthYear);
      }
      
      // Fetch records for the last 3 months
      const payrollPromises = last3Months.map(monthYear => {
        const q = query(
          payrollRecordsCollectionRef,
          where("staffId", "==", userData.id),
          where("monthYear", "==", monthYear)
        );
        return getDocs(q);
      });
      
      const results = await Promise.all(payrollPromises);
      const records = [];
      
      results.forEach((snap, index) => {
        if (!snap.empty) {
          const doc = snap.docs[0];
          records.push({
            id: doc.id,
            ...doc.data(),
            monthYear: last3Months[index]
          });
        } else {
          // Add placeholder for missing months
          records.push({
            monthYear: last3Months[index],
            exists: false
          });
        }
      });
      
      // Sort by monthYear (newest first)
      records.sort((a, b) => b.monthYear.localeCompare(a.monthYear));
      setPayrollRecords(records);
      
    } catch (err) {
      console.error("Error loading payroll records:", err);
      setError("Failed to load payroll records");
      setShowModal(true);
    } finally {
      setLoadingPayroll(false);
    }
  }; 
  
  // Helper function to get salary component by ID
  const getComponent = (compId) => {
    return salaryComponents.find(c => c.id === compId) || { 
      name: 'Unknown Component', 
      type: 'addition', 
      calculation: 'fixed' 
    };
  };

  // Function to calculate pay from payroll record
  const calculatePayFromRecord = (record) => {
    if (!record || !record.staffSnapshot) return null;
    
    const staffSnapshot = record.staffSnapshot;
    const extras = record.extras || [];
    
    // Get rank data - prioritize from record, fall back to current rankData
    let rank = record.staffSnapshot.rankData;
    if (!rank) {
      rank = rankData;
    }
    
    if (!rank) return null;
    
    const basic = parseFloat(rank.basicSalary) || 0;
    let gross = basic;
    let deductions = 0;
    const items = { basic };

    // Calculate rank components (from rank.components array)
    if (rank.components && Array.isArray(rank.components)) {
      rank.components.forEach(c => {
        const comp = getComponent(c.id);
        if (!comp) return;
        
        const val = parseFloat(c.value) || 0;
        let amt = val;
        
        // Handle percentage calculations
        if (comp.calculation === 'percentage') {
          if (comp.percentageOf === 'basic') {
            amt = basic * val / 100;
          } else if (comp.percentageOf === 'gross') {
            // For gross percentage, we need to calculate iteratively
            // This is simplified - actual implementation might need adjustment
            amt = gross * val / 100;
          }
        }
        
        if (comp.type === 'addition') {
          gross += amt;
          items[comp.name?.toUpperCase() || 'ADDITION'] = amt;
        } else if (comp.type === 'subtraction') {
          deductions += amt;
          items[comp.name?.toUpperCase() || 'DEDUCTION'] = -amt;
        }
      });
    }

    // Calculate extras (from payroll record extras array)
    if (Array.isArray(extras)) {
      extras.forEach(e => {
        const val = parseFloat(e.value) || 0;
        let amt = val;
        
        // Handle percentage calculations for extras
        if (e.calculation === 'percentage') {
          if (e.percentageOf === 'basic') {
            amt = basic * val / 100;
          } else if (e.percentageOf === 'gross') {
            amt = gross * val / 100;
          }
        }
        
        if (e.type === 'addition') {
          gross += amt;
          items[e.name?.toUpperCase() || 'BONUS'] = amt;
        } else if (e.type === 'deduction' || e.type === 'subtraction') {
          deductions += amt;
          items[e.name?.toUpperCase() || 'DEDUCTION'] = -amt;
        }
      });
    }

    const net = gross - deductions;
    return { basic, gross, deductions, net, items };
  };

  // Function to format monthYear for display
  const formatMonthYear = (monthYear) => {
    if (!monthYear) return '';
    const [year, month] = monthYear.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const generatePaySlipPDF = (record) => {
    if (!record || !record.staffSnapshot) {
      setError("No payroll data available for this month");
      setShowModal(true);
      return;
    }
    
    const doc = new jsPDF();
    const calculatedPay = calculatePayFromRecord(record);
    if (!calculatedPay) {
      setError("Unable to calculate payroll for this record");
      setShowModal(true);
      return;
    }
    
    const { gross, deductions, net, items } = calculatedPay;
    const monthName = formatMonthYear(record.monthYear);
    const [year] = record.monthYear.split('-');
    const generatedDate = new Date().toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).replace(/,/g, '');

    // Use the same addHeader function from Payroll
    const addHeader = (doc) => {
      doc.setFillColor(226, 232, 240);
      doc.rect(0, 0, doc.internal.pageSize.width, 50, 'F');
      try { doc.addImage(ShopLogo, 'PNG', 15, 8, 38, 38); } catch (err) { console.log("Logo not loaded"); }
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
      doc.text(SHOP_NAME.toUpperCase(), 60, 20);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text(SHOP_ADDRESS.toUpperCase(), 60, 28);
      doc.text(SHOP_PHONE, 60, 35);
      doc.setTextColor(0, 0, 0);
    };

    addHeader(doc);

    // Employee Information Section
    doc.setFillColor(245, 245, 245);
    doc.rect(15, 55, 180, 30, 'F');
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('STAFF INFORMATION', 20, 65);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Name: ${userData.firstName.toUpperCase()} ${userData.lastName.toUpperCase()}`, 20, 72);
    doc.text(`Department: ${userData.department || 'N/A'}`, 20, 77);
    doc.text(`Bank: ${userData.bankName || 'N/A'}`, 100, 72);
    doc.text(`Account: ${userData.accountNumber || 'N/A'}`, 100, 77);
    doc.text(`Period: ${monthName}, ${year}`, 150, 77);

    let y = 95;

    // Earnings Table - Only show if there are earnings
    const hasEarnings = Object.entries(items).some(([_, amt]) => amt > 0);

    if (hasEarnings) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('EARNINGS', 15, y);
      y += 8;

      const earningsData = Object.entries(items)
        .filter(([_, amt]) => amt > 0)
        .map(([name, amt], idx) => [
          idx + 1,
          name.toUpperCase(),
          `N${amt.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
        ]);

      autoTable(doc, {
        startY: y,
        head: [['S/N', 'Description', 'Amount']],
        body: earningsData,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2, textColor: 0, },
        headStyles: { 
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center' },
          1: { cellWidth: 125 },
          2: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 15, right: 15 }
      });

      y = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text('No earnings for this period', 15, y);
      y += 15;
    }

    // Deductions Table - Only show if there are deductions
    const hasDeductions = Object.entries(items).some(([_, amt]) => amt < 0);

    if (hasDeductions) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('DEDUCTIONS', 15, y);
      y += 8;

      const deductionsData = Object.entries(items)
        .filter(([_, amt]) => amt < 0)
        .map(([name, amt], idx) => [
          idx + 1,
          name.toUpperCase(),
          `N${(-amt).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
        ]);

      autoTable(doc, {
        startY: y,
        head: [['S/N', 'Description', 'Amount']],
        body: deductionsData,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2, textColor: 0 },
        headStyles: { 
          fillColor: [231, 76, 60],
          textColor: 255,
          fontStyle: 'bold',
        },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center' },
          1: { cellWidth: 125 },
          2: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 15, right: 15 }
      });

      y = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('No deductions for this period', 15, y);
      y += 15;
    }

    // Summary Section
    doc.setFillColor(71, 85, 105); 
    doc.rect(15, y, 180, 28, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('PAYMENT SUMMARY', 20, y + 8);

    doc.setFontSize(10);
    doc.text('Gross Pay:', 120, y + 8);
    doc.text(`N${gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 8, { align: 'right' });
    
    doc.text('Total Deductions:', 120, y + 15);
    doc.text(`N${deductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 15, { align: 'right' });
    
    doc.setFontSize(14);
    doc.text('Net Pay:', 120, y + 22);
    doc.text(`N${net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 22, { align: 'right' });

    // Footer
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.text(`Generated on: ${generatedDate}`, 105, 285, { align: 'center' });

    // Save the PDF
    doc.save(`PaySlip_${userData.firstName}_${userData.lastName}_${monthName}_${year}.pdf`);
  };

  // Function to view pay slip details
  const viewPaySlip = (record) => {
    setSelectedPaySlip(record);
    setIsPaySlipModalOpen(true);
  };

  if (loading) return <Loader text="Loading Dashboard" />;

  // Check if user has access
  if (!userData || !['Accountant', 'Admin', 'Supreme', 'Cashier', 'Staff'].includes(userData.status))
    return <p className="male-female-stat align-center justify-center">Access Denied</p>;

  const COLORS = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6'];

  // Format date for display - handles both date strings and Firestore Timestamps
  const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    
    let date;
    
    // Check if it's a Firestore Timestamp
    if (dateInput && typeof dateInput === 'object' && dateInput.toDate) {
      // It's a Firestore Timestamp
      date = dateInput.toDate();
    } else if (dateInput && typeof dateInput === 'object' && dateInput.seconds) {
      // It's a Firestore Timestamp from snapshot data
      date = new Date(dateInput.seconds * 1000);
    } else if (dateInput && typeof dateInput === 'object' && dateInput._seconds) {
      // Alternative Firestore Timestamp format
      date = new Date(dateInput._seconds * 1000);
    } else {
      // Try to parse as regular date string
      date = new Date(dateInput);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-NG', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Format currency with Naira sign and commas
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `₦${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Staff Profile View
  if (userData.status === 'Staff' || userData?.status === 'Cashier') {
    return (
      <div className="dashboard-container padding-bottom-d">
        <div className="pos-dashboard" style={{ width: '100%', maxWidth: '1100px', margin: 'auto' }}>          
          {/* Profile Info Cards - Using KPI styling */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px', marginBottom: '25px' }}>
            {/* Personal Information Card */}
            <div
              className="hover-lift"
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '16px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                borderLeft: `6px solid #3498db`,
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-8px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div className="row space-between align-center">
                <div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Full Name</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2c3e50' }}>
                    {`${userData.firstName} ${userData.lastName}` || 'Not Provided'}
                  </div>
                </div>
                <i className="fas fa-user fa-2x" style={{ opacity: 0.2, color: '#3498db' }}></i>
              </div>
            </div>

            {/* Contact Information Card */}
            <div
              className="hover-lift"
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '16px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                borderLeft: `6px solid #2ecc71`,
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-8px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div className="row space-between align-center">
                <div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Email Address</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2c3e50', wordBreak: 'break-word' }}>
                    {user.email || 'Not Provided'}
                  </div>
                </div>
                <i className="fas fa-envelope fa-2x" style={{ opacity: 0.2, color: '#2ecc71' }}></i>
              </div>
            </div>

            {/* Phone Number Card */}
            <div
              className="hover-lift"
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '16px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                borderLeft: `6px solid #f39c12`,
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-8px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div className="row space-between align-center">
                <div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Phone Number</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2c3e50' }}>
                    {userData.phone || 'Not Provided'}
                  </div>
                </div>
                <i className="fas fa-phone fa-2x" style={{ opacity: 0.2, color: '#f39c12' }}></i>
              </div>
            </div>

            {/* Rank/Position Card */}
            <div
              className="hover-lift"
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '16px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                borderLeft: `6px solid #9b59b6`,
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-8px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div className="row space-between align-center">
                <div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Position / Rank</div>
                  <div style={{ fontSize: loadingRank ? '16px' : '20px', fontWeight: 'bold', color: '#2c3e50' }}>
                    {loadingRank ? 'Loading...' : (rankData?.name || userData.rankId || 'Not Assigned')}
                  </div>
                </div>
                <i className="fas fa-briefcase fa-2x" style={{ opacity: 0.2, color: '#9b59b6' }}></i>
              </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '20px',
            marginBottom: '25px'
          }}>
            {/* First Appointment Date */}
            <div style={{ 
              background: 'white', 
              padding: '25px', 
              borderRadius: '16px', 
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              borderLeft: `6px solid #e74c3c`
            }}>
              <div style={{ marginBottom: '15px' }}>
                <i className="fas fa-calendar-alt fa-lg" style={{ color: '#e74c3c', marginRight: '10px' }}></i>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>Date of First Appointment</span>
              </div>
              <div style={{ fontSize: '18px', color: '#34495e', paddingLeft: '10px' }}>
                {formatDate(userData.dateOfFirstEmployment)}
              </div>
              <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '10px' }}>
                {userData.dateOfFirstEmployment 
                  ? 'Your official start date with the organization' 
                  : 'Start date not specified'}
              </p>
            </div>

            {/* Employee ID / Staff ID */}
            <div style={{ 
              background: 'white', 
              padding: '25px', 
              borderRadius: '16px', 
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              borderLeft: `6px solid #1abc9c`
            }}>
              <div style={{ marginBottom: '15px' }}>
                <i className="fas fa-id-card fa-lg" style={{ color: '#1abc9c', marginRight: '10px' }}></i>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>Employee ID</span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#34495e', paddingLeft: '10px' }}>
                {userData.uid || 'N/A'}
              </div>
              <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '10px' }}>
                Your unique identification number
              </p>
            </div>
          </div>

          {/* Rank Details Section */}
          <div style={{ 
            background: 'white', 
            padding: '25px', 
            borderRadius: '16px', 
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            marginBottom: '25px'
          }}>
            <h3 style={{ margin: '0 0 20px', color: '#2c3e50', borderBottom: '2px solid #f1f3f5', paddingBottom: '10px' }}>
              <i className="fas fa-award" style={{ marginRight: '10px', color: '#f39c12' }}></i>
              Rank & Salary Details
            </h3>
            
            {loadingRank ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading rank details...</span>
                </div>
                <p style={{ marginTop: '15px', color: '#7f8c8d' }}>Loading rank information...</p>
              </div>
            ) : rankData ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Your current rank</div>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#2c3e50' }}>
                    {rankData.name || 'N/A'}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Grade Level</div>
                  <div style={{ 
                    fontSize: '22px', 
                    fontWeight: 'bold', 
                    color: '#3498db',
                    background: '#ebf5fb',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    display: 'inline-block'
                  }}>
                    {rankData.gradeLevel || 'N/A'}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Basic Salary</div>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#27ae60',
                    background: '#d5f4e6',
                    padding: '10px 20px',
                    borderRadius: '8px'
                  }}>
                    {formatCurrency(rankData.basicSalary)}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#95a5a6' }}>
                <i className="fas fa-exclamation-circle fa-3x" style={{ marginBottom: '15px', opacity: 0.5 }}></i>
                <p>Rank information not available</p>
                {userData.rankId && (
                  <p style={{ fontSize: '14px', marginTop: '5px' }}>
                    Rank ID: {userData.rankId}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Department/Additional Info */}
          <div style={{ 
            background: 'white', 
            padding: '25px', 
            borderRadius: '16px', 
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            marginBottom: '25px'
          }}>
            <h3 style={{ margin: '0 0 20px', color: '#2c3e50', borderBottom: '2px solid #f1f3f5', paddingBottom: '10px' }}>
              <i className="fas fa-building" style={{ marginRight: '10px', color: '#3498db' }}></i>
              Department Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '5px' }}>Department</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                  {userData.department || 'Not Assigned'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '5px' }}>Status</div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: '#27ae60',
                  background: '#d5f4e6',
                  padding: '5px 15px',
                  borderRadius: '20px',
                  display: 'inline-block'
                }}>
                  {userData.status}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '5px' }}>Last Login</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
                  {formatDate(userData.lastLogin || new Date().toISOString())}
                </div>
              </div>
            </div>
          </div>

          {/* Pay Slip History Section */}
          <div style={{ 
            background: 'white', 
            padding: '25px', 
            borderRadius: '16px', 
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            marginBottom: '25px'
          }}>
            <h3 style={{ margin: '0 0 20px', color: '#2c3e50', borderBottom: '2px solid #f1f3f5', paddingBottom: '10px' }}>
              <i className="fas fa-file-invoice-dollar" style={{ marginRight: '10px', color: '#3498db' }}></i>
              Pay Slip History (Last 3 Months)
            </h3>
            
            {loadingPayroll ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading pay slips...</span>
                </div>
                <p style={{ marginTop: '15px', color: '#7f8c8d' }}>Loading pay slip history...</p>
              </div>
            ) : payrollRecords.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#95a5a6' }}>
                <i className="fas fa-file-alt fa-3x" style={{ marginBottom: '15px', opacity: 0.5 }}></i>
                <p>No pay slip history available</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {payrollRecords.map((record, index) => {
                  const calculatedPay = calculatePayFromRecord(record);
                  const monthName = formatMonthYear(record.monthYear);
                  
                  return (
                    <div
                      key={index}
                      style={{
                        background: record.exists === false ? '#f8f9fa' : '#ffffff',
                        border: '1px solid #e9ecef',
                        borderRadius: '12px',
                        padding: '20px',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.3s ease'
                      }}
                      className={record.exists !== false ? 'hover-lift' : ''}
                    >
                      <div className="row align-center space-between">
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                            {monthName}
                          </div>
                          {record.exists === false ? (
                            <div style={{ fontSize: '14px', color: '#95a5a6', marginTop: '5px' }}>
                              No pay slip generated for this month
                            </div>
                          ) : calculatedPay ? (
                            <>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginTop: '5px' }}>
                              Net Pay: <span style={{ fontWeight: 'bold', color: '#27ae60' }}>
                                ₦{calculatedPay.net.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                            </div>                        
                          
                          </>
                        ) : null}
                        </div>
                        {record.exists !== false && calculatedPay && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => generatePaySlipPDF(record)}
                              className="list-btn"
                              style={{
                                marginTop: '10px',
                              }}
                            >
                              <i className="fas fa-print"></i>
                            </button>
                          </div>
                        )}
                      </div>                      
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Original Dashboard for Accountant, Admin, Supreme
  return (
    <>
      <div className="dashboard-container padding-bottom-d">
        <div className="pos-dashboard" style={{ width: '100%', maxWidth: '1100px', margin: 'auto' }}>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px', marginBottom: '25px' }}>
            {[
              { label: "Revenue", value: data.summary.revenue, color: "#3A7CA5", icon: "fa-arrow-trend-up" },
              { label: "Expenses", value: data.summary.expenses, color: "#E56B6F", icon: "fa-arrow-trend-down" },
              { label: "Net Profit", value: data.summary.profit, color: data.summary.profit >= 0 ? "#27ae60" : "#c0392b", icon: "fa-sack-dollar" },
              { label: "Cash Balance", value: data.summary.cash, color: "#F39C12", icon: "fa-wallet" }
            ].map((kpi, i) => (
              <div
                key={i}
                className="hover-lift"
                style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '16px',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                  borderLeft: `6px solid ${kpi.color}`,
                  transition: 'all 0.3s',
                  cursor: 'pointer'
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-8px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="row space-between align-center">
                  <div>
                    <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>{kpi.label}</div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: kpi.color }}>
                      N{kpi.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <i className={`fas ${kpi.icon} fa-2x`} style={{ opacity: 0.2, color: kpi.color }}></i>
                </div>
              </div>
            ))}
          </div>

          {/* ULTRA RESPONSIVE CHARTS */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            marginBottom: '25px'
          }}>
            {/* Profit Trend */}
            <div style={{ background: 'white', padding: '20px', borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)', minHeight: '320px' }}>
              <h3 style={{ margin: '0 0 15px', color: '#2c3e50' }}>Profit Trend</h3>
              {data.profitTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={270}>
                  <LineChart data={data.profitTrend}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(v) => `N${Number(v).toLocaleString()}`} />
                    <Line type="monotone" dataKey="profit" stroke="#27ae60" strokeWidth={4} dot={{ fill: '#27ae60' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p style={{ textAlign: 'center', color: '#95a5a6', padding: '70px 0' }}>No data</p>}
            </div>

            {/* Top Expenses */}
            <div style={{ background: 'white', padding: '20px', borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)', minHeight: '320px' }}>
              <h3 style={{ margin: '0 0 15px', color: '#2c3e50' }}>Top Expense Categories</h3>
              {data.topExpenses.length > 0 ? (
                <ResponsiveContainer width="100%" height={270}>
                  <PieChart>
                    <Pie
                      data={data.topExpenses}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, value }) => `${name}: N${value.toLocaleString()}`}
                    >
                      {data.topExpenses.map((entry, i) => (
                        <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `N${v.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p style={{ textAlign: 'center', color: '#95a5a6', padding: '70px 0' }}>No expenses</p>}
            </div>
          </div>

          {/* Recent Transactions */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 15px', color: '#2c3e50' }}>Recent Transactions</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentTransactions.map(t => (
                    <tr key={t.id} className="hover-row" style={{ cursor: 'pointer', transition: '0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f3f5'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td style={{ padding: '12px' }}>{t.displayDate}</td>
                      <td style={{ padding: '12px' }}>{t.description || 'Journal Entry'}</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#27ae60' }}>
                        N{t.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>          
        </div>
      </div>

      <style jsx>{`
        .hover-lift { transition: all 0.3s ease; }
        .hover-row { transition: background-color 0.2s; }
        .spinner-border {
          display: inline-block;
          width: 2rem;
          height: 2rem;
          vertical-align: text-bottom;
          border: 0.25em solid currentColor;
          border-right-color: transparent;
          border-radius: 50%;
          animation: spinner-border .75s linear infinite;
        }
        @keyframes spinner-border {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Pay Slip Details Modal */}
      <Modal 
        isOpen={isPaySlipModalOpen} 
        onRequestClose={() => setIsPaySlipModalOpen(false)}
        className="custom-modal modal-scroll"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-scroll">
          <h2 className="caps size5 bold text-center">
            PAY SLIP DETAILS - {selectedPaySlip ? formatMonthYear(selectedPaySlip.monthYear) : ''}
          </h2>
          
          {selectedPaySlip && (() => {
            const calculatedPay = calculatePayFromRecord(selectedPaySlip);
            if (!calculatedPay) return <p>No pay data available</p>;
            
            const { basic, gross, deductions, net, items } = calculatedPay;
            
            return (
              <div>
                <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ margin: '0 0 5px' }}>
                    <span className="bold">{userData.firstName} {userData.lastName}</span>
                  </p>
                  <p style={{ margin: '0 0 5px' }}>
                    <span className="size5">Department: {userData.department || 'N/A'}</span>
                  </p>
                  <p style={{ margin: '0' }}>
                    <span className="size5">Employee ID: {userData.uid || 'N/A'}</span>
                  </p>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    background: '#27ae60', 
                    color: 'white', 
                    padding: '15px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', marginBottom: '5px' }}>Net Pay</div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                      ₦{net.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>

                {/* Earnings */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 className="caps bold size5 font-teal" style={{ marginBottom: '10px' }}>EARNINGS</h3>
                  <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                    <div className="row justify-between" style={{ marginBottom: '8px' }}>
                      <span>Basic Salary:</span>
                      <span className="bold">₦{basic.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    
                    {Object.entries(items)
                      .filter(([name, amt]) => amt > 0 && name !== 'BASIC')
                      .map(([name, amt], i) => (
                        <div key={i} className="row justify-between" style={{ marginBottom: '8px' }}>
                          <span>+ {name.replace(/_/g, ' ')}:</span>
                          <span className="bold font-green">₦{amt.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                    
                    <div className="row justify-between" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #dee2e6' }}>
                      <span className="bold">Gross Pay:</span>
                      <span className="bold">₦{gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
                
                {/* Deductions */}
                {deductions > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 className="caps bold size5 font-red" style={{ marginBottom: '10px' }}>DEDUCTIONS</h3>
                    <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                      {Object.entries(items)
                        .filter(([_, amt]) => amt < 0)
                        .map(([name, amt], i) => (
                          <div key={i} className="row justify-between" style={{ marginBottom: '8px' }}>
                            <span>− {name}:</span>
                            <span className="bold font-red">₦{(-amt).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                      
                      <div className="row justify-between" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #dee2e6' }}>
                        <span className="bold">Total Deductions:</span>
                        <span className="bold">₦{deductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                  <button 
                    className="login-button caps" 
                    onClick={() => setIsPaySlipModalOpen(false)}
                  >
                    Close
                  </button>
                  <button 
                    className="login-button login-button-d caps" 
                    onClick={() => {
                      generatePaySlipPDF(selectedPaySlip);
                      setIsPaySlipModalOpen(false);
                    }}
                  >
                    Print Pay Slip
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </Modal>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || "Dashboard refreshed!"}
          onClose={() => { setError(""); setShowModal(false); }}
        />
      )}
    </>
  );
};

export default Dashboard;